import React, { useMemo, useState } from "react"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { AlertContainer } from "@nova/component-alert-container"
import { ButtonPrimary, ButtonTertiary } from "@nova/component-btn"
import { SelectOutlined, type GroupedSelectOptions } from "@nova/component-select"
import { ToggleSwitch } from "@nova/component-toggle-switch"
import { Input } from "@nova/ds-component-input"
import { formatDate, formatTime, DateFormat } from "@nova/format"
import { fetchApiGateway } from "@nova/fetch-api-gateway"
import { Aircraft, DroneManufacturer } from "@nova/schemas"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useAircraft } from "features/pilot-tracking/aircraft/AircraftQueries"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { Spinner, ThemeSizes } from "themes/default-tailwind"
import { selectUserID } from "user/profile/selectors"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { usePluginState } from "../../usePluginState"
import { hideGlobalModal } from "runlevel/actions"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { useRouter } from "pages"
import { ExtraSmallIcon, OpenInNew } from "@nova/component-icons"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { setGlobalNotificationContext } from "user/profile/slice"

enum SkydioLiveStreamModalMode {
    Loading = "loading",
    SetupLiveStream = "setupLiveStream",
    ConfigureSkydioAccount = "configureSkydioAccount",
}

const getModalTitle = (mode: SkydioLiveStreamModalMode) => {
    switch (mode) {
        case SkydioLiveStreamModalMode.SetupLiveStream:
            return "Start a Live Stream"
        case SkydioLiveStreamModalMode.ConfigureSkydioAccount:
            return "Configure Skydio Account"
        default:
            return ""
    }
}

const makeDroneOption = (a: Aircraft) => ({
    label: (
        <div className="flex flex-col gap-0.5">
            <span>{a.nickname ?? a.model ?? a.serialNumber}</span>
            <span className="text-xs text-gray-400">{a.model ?? a.serialNumber}</span>
        </div>
    ),
    searchableText: [a.nickname, a.model, a.serialNumber].filter(Boolean).join(" "),
    value: a.id,
})

export const SkydioLiveStreamModal = () => {
    const { setSelectedPlugin } = usePluginState()
    const dispatch = useAppDispatch()
    const router = useRouter("/workspace/:workspaceId")
    const { aircraft, isLoading } = useAircraft()
    const orgId = useAppSelector(selectActiveWorkspaceId())
    const projectId = useAppSelector(selectActivatedFireId)
    const userId = useAppSelector(selectUserID)
    const [selectedAircraftId, setSelectedAircraftId] = useState<string | null>(null)
    const [streamName, setStreamName] = useState("")
    const [saveStream, setSaveStream] = useState(true)
    const [isConnecting, setIsConnecting] = useState(false)
    const [connectError, setConnectError] = useState<string | null>(null)

    const close = () => {
        dispatch(hideGlobalModal())
        setSelectedPlugin(undefined)
    }

    const skydioAircraft = useMemo(
        () => aircraft.filter((a) => a.manufacturer === DroneManufacturer.Skydio),
        [aircraft]
    )

    const droneOptions = useMemo<GroupedSelectOptions<string, never>>(() => {
        const recentIds: string[] = JSON.parse(
            localStorage.getItem("recentlyConnectedSkydioDrones") ?? "[]"
        )

        const recentSkydioAircraft = recentIds
            .map((id) => skydioAircraft.find((a) => a.id === id))
            .filter((a): a is Aircraft => a !== undefined)

        const otherSkydioAircraft = skydioAircraft
            .filter((a) => !recentIds.includes(a.id))
            .sort((a, b) =>
                (a.nickname ?? a.model ?? a.serialNumber).localeCompare(
                    b.nickname ?? b.model ?? b.serialNumber
                )
            )

        return [
            ...(recentSkydioAircraft.length > 0
                ? [
                      {
                          sectionTitle: "Recently Used",
                          options: recentSkydioAircraft.map(makeDroneOption),
                      },
                  ]
                : []),
            { sectionTitle: "All Drones", options: otherSkydioAircraft.map(makeDroneOption) },
        ]
    }, [skydioAircraft])

    // TODO: filter to only API connected Skydio drones
    const hasSkydioAircraft = skydioAircraft.length > 0

    const selectedAircraft = skydioAircraft.find((a) => a.id === selectedAircraftId)

    const modalMode = useMemo(() => {
        if (isLoading) return SkydioLiveStreamModalMode.Loading
        if (hasSkydioAircraft) return SkydioLiveStreamModalMode.SetupLiveStream
        return SkydioLiveStreamModalMode.ConfigureSkydioAccount
    }, [hasSkydioAircraft, isLoading])

    const handleAircraftSelection = (aircraftId: string) => {
        setSelectedAircraftId(aircraftId)
    }

    const handleConnect = async () => {
        if (!selectedAircraft || !orgId || !projectId || !userId) return
        setIsConnecting(true)

        const now = new Date()
        const layerName =
            streamName.trim() ||
            `Live Stream - ${formatDate({ value: now, format: DateFormat.DayMonthYear })} ${formatTime({ value: now })}`

        try {
            const response = await fetchApiGateway(
                "post /integrations/skydio/org/:orgId/live-stream/initialize",
                {
                    params: { orgId },
                    body: {
                        droneSN: selectedAircraft.serialNumber,
                        projectId,
                        organizationId: orgId,
                        userId,
                        saveStream,
                        layerName,
                    },
                }
            )
            if (!response.isCurrentlyLiveStreaming) {
                const layerCount = Object.keys(response.layers ?? {}).length
                const label = layerCount === 1 ? "Layer" : "Layers"
                dispatch(
                    setGlobalNotificationContext({
                        message: `${label} created. Waiting for live stream to begin.`,
                        status: GlobalNotificationStatus.success,
                        timeoutInMs: 4000,
                    })
                )
            }

            const recentKey = "recentlyConnectedSkydioDrones"
            const existing: string[] = JSON.parse(localStorage.getItem(recentKey) ?? "[]")
            const updated = [
                selectedAircraft.id,
                ...existing.filter((id) => id !== selectedAircraft.id),
            ].slice(0, 5)
            localStorage.setItem(recentKey, JSON.stringify(updated))
            close()
        } catch (err) {
            console.error("[Skydio] failed to initialize live stream:", err)
            setIsConnecting(false)
            const userMessage =
                err &&
                typeof err === "object" &&
                "userMessage" in err &&
                typeof err.userMessage === "string"
                    ? err.userMessage
                    : null
            setConnectError(userMessage ?? "Failed to connect the live stream. Please try again.")
        }
    }

    return (
        <HighPriorityNovaModalContainer
            title={getModalTitle(modalMode)}
            className="w-[34rem]"
            canCloseModal
            closeModal={close}
            onReturn={() =>
                dispatch(
                    initializeCenterModalForComponentAction(ModalNames.ChooseLiveVideoTypeModal)
                )
            }
        >
            <div className="flex flex-col gap-3 pt-4 text-gray-0">
                {modalMode === SkydioLiveStreamModalMode.Loading ? (
                    <div className="flex items-center justify-center min-h-[8rem]">
                        <Spinner size={ThemeSizes.Medium} />
                    </div>
                ) : (
                    <>
                        {modalMode === SkydioLiveStreamModalMode.SetupLiveStream && (
                            <>
                                {connectError && (
                                    <AlertContainer
                                        type="error"
                                        title="Unable to start live stream"
                                        body={connectError}
                                        onClose={() => setConnectError(null)}
                                    />
                                )}
                                <label className="text-sm text-gray-200">Drone*</label>
                                <SelectOutlined
                                    options={droneOptions}
                                    value={selectedAircraftId ?? ""}
                                    onChange={handleAircraftSelection}
                                    placeholder="Select a drone"
                                    disabled={!hasSkydioAircraft}
                                    searchable
                                    getTitle={(id) => {
                                        const a = skydioAircraft.find((x) => x.id === id)
                                        return a ? (a.nickname ?? a.model ?? a.serialNumber) : ""
                                    }}
                                />
                                <Input
                                    label="Stream Name"
                                    placeholder="Live Stream [Date]"
                                    value={streamName}
                                    onChange={(e) => setStreamName(e.target.value)}
                                />
                                <div className="flex items-center gap-2">
                                    <label className="text-sm text-gray-200">Auto Save Video</label>
                                    <ToggleSwitch
                                        isToggled={saveStream}
                                        setIsToggled={setSaveStream}
                                    />
                                </div>
                                <hr className="border-gray-600" />
                                <p className="text-sm text-gray-300 mb-8 text-center">
                                    {"Don't see your drone here? Sync with Skydio via "}
                                    <button
                                        className="text-blue-300 underline inline-flex items-center gap-1"
                                        onClick={() =>
                                            router.navigate({
                                                path: "/settings/connected-accounts",
                                            })
                                        }
                                    >
                                        Settings
                                        <ExtraSmallIcon Icon={OpenInNew} />
                                    </button>
                                </p>
                                <div className="flex gap-2 ml-auto">
                                    <ButtonTertiary title="Close" onClick={close} />
                                    <ButtonPrimary
                                        title="Connect Stream"
                                        onClick={() => void handleConnect()}
                                        disabled={!selectedAircraftId || isConnecting}
                                    />
                                </div>
                            </>
                        )}

                        {modalMode === SkydioLiveStreamModalMode.ConfigureSkydioAccount && (
                            <div className="flex flex-col gap-4 py-2">
                                <AlertContainer
                                    className="!gap-0"
                                    type="info"
                                    title="No Skydio drones are linked with this workspace"
                                />
                                <p className="text-sm text-gray-0">
                                    Connect an account to access your drones and start a live
                                    stream.
                                </p>
                                <ButtonPrimary
                                    title="Connect Skydio Account →"
                                    className="self-start"
                                    onClick={() => {
                                        close()
                                        router.navigate({ path: "/settings/connected-accounts" })
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </HighPriorityNovaModalContainer>
    )
}
